import type {
	FluidConfig,
	WebGLExtensions,
	DoubleFBO,
	FBO,
} from "../types/fluid-types.js"
import { getWebGLContext } from "./webgl-context.js"
import { createBasicProgram, createMaterial } from "./program.js"
import {
	createDoubleFBO,
	createFBO,
	blit,
	initBlitBuffer,
	getResolution,
} from "./fbo.js"
import { PointerTracker } from "../utils/pointer-tracker.js"
import { ColorManager, parseCSSColor } from "../utils/color-utils.js"
import * as shaders from "./shaders.js"

export class FluidSimulation {
	private gl!: WebGLRenderingContext | WebGL2RenderingContext
	private ext!: WebGLExtensions
	private config: FluidConfig

	// Framebuffers
	private velocityFBO!: DoubleFBO
	private dyeFBO!: DoubleFBO
	private pressureFBO!: DoubleFBO
	private divergenceFBO!: FBO
	private curlFBO!: FBO

	// Shader programs
	private clearProgram!: any
	private splatProgram!: any
	private advectionProgram!: any
	private divergenceProgram!: any
	private curlProgram!: any
	private vorticityProgram!: any
	private pressureProgram!: any
	private gradientSubtractProgram!: any
	private displayMaterial!: any

	// Utilities
	private pointerTracker!: PointerTracker
	private colorManager!: ColorManager

	// Timing
	private lastTime = 0
	private canvas: HTMLCanvasElement

	constructor(canvas: HTMLCanvasElement, config: FluidConfig) {
		this.canvas = canvas
		this.config = { ...config }
		console.log(config)
	}

	async initialize(): Promise<void> {
		// Initialize WebGL context
		const context = getWebGLContext(this.canvas)
		if (!context) {
			throw new Error("Failed to initialize WebGL context")
		}

		this.gl = context.gl
		this.ext = context.ext

		// Initialize vertex buffer
		initBlitBuffer(this.gl)

		// Compile shaders
		this.initializeShaders()

		// Initialize framebuffers
		this.initializeFBOs()

		// Initialize utilities
		this.initializeUtilities()

		// Add initial splats
		this.addRandomSplats(Math.floor(Math.random() * 20) + 5)
	}

	private initializeShaders(): void {
		const gl = this.gl

		// Create base vertex shader
		const baseVertexShader = shaders.baseVertexShader

		// Create all shader programs
		this.clearProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.clearShader
		)
		this.splatProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.splatShader
		)
		this.divergenceProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.divergenceShader
		)
		this.curlProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.curlShader
		)
		this.vorticityProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.vorticityShader
		)
		this.pressureProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.pressureShader
		)
		this.gradientSubtractProgram = createBasicProgram(
			gl,
			baseVertexShader,
			shaders.gradientSubtractShader
		)

		// Create advection program with manual filtering support
		const advectionKeywords = this.ext.supportLinearFiltering
			? []
			: ["MANUAL_FILTERING"]

		this.advectionProgram = createMaterial(
			gl,
			baseVertexShader,
			shaders.advectionShader
		)
		this.advectionProgram.setKeywords(advectionKeywords)

		// Create display material
		this.displayMaterial = createMaterial(
			gl,
			baseVertexShader,
			shaders.displayShaderSource
		)
	}

	private initializeFBOs(): void {
		const gl = this.gl
		const simRes = getResolution(gl, this.config.SIM_RESOLUTION)
		const dyeRes = getResolution(gl, this.config.DYE_RESOLUTION)

		const texType = this.ext.halfFloatTexType
		const rgba = this.ext.formatRGBA
		const rg = this.ext.formatRG
		const r = this.ext.formatR
		const filtering = this.ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST

		if (!rgba || !rg || !r) {
			throw new Error("Required texture formats not supported")
		}

		this.velocityFBO = createDoubleFBO(
			gl,
			simRes.width,
			simRes.height,
			rg.internalFormat,
			rg.format,
			texType,
			filtering
		)

		this.dyeFBO = createDoubleFBO(
			gl,
			dyeRes.width,
			dyeRes.height,
			rgba.internalFormat,
			rgba.format,
			texType,
			filtering
		)

		this.pressureFBO = createDoubleFBO(
			gl,
			simRes.width,
			simRes.height,
			r.internalFormat,
			r.format,
			texType,
			gl.NEAREST
		)

		this.divergenceFBO = createFBO(
			gl,
			simRes.width,
			simRes.height,
			r.internalFormat,
			r.format,
			texType,
			gl.NEAREST
		)

		this.curlFBO = createFBO(
			gl,
			simRes.width,
			simRes.height,
			r.internalFormat,
			r.format,
			texType,
			gl.NEAREST
		)
	}

	private initializeUtilities(): void {
		this.colorManager = new ColorManager(this.config.COLOR_MODE)

		// Set fixed color if provided
		if (this.config.COLOR) {
			const parsedColor = parseCSSColor(this.config.COLOR)
			if (parsedColor) {
				this.colorManager.setFixedColor(parsedColor)
			} else {
				console.warn(`Invalid color value: ${this.config.COLOR}`)
			}
		}

		this.pointerTracker = new PointerTracker(this.canvas, {
			onSplat: (pointer) => {
				this.splat(
					pointer.texcoordX,
					pointer.texcoordY,
					pointer.deltaX * this.config.SPLAT_FORCE,
					pointer.deltaY * this.config.SPLAT_FORCE,
					pointer.color
				)
			},
		})
	}

	step(): void {
		const dt = this.calcDeltaTime()

		if (this.config.PAUSED) return

		// Ensure utilities are initialized
		if (!this.colorManager || !this.pointerTracker) {
			console.warn("Simulation not fully initialized, skipping step")
			return
		}

		// Update colors
		const currentColor = this.colorManager.updateColors(
			dt,
			this.config.COLORFUL,
			this.config.COLOR_UPDATE_SPEED,
			this.config.COLOR_TRANSITION_SPEED
		)

		// Update pointer colors
		this.pointerTracker.setPointerColor(currentColor)

		// Update pointers and create splats
		this.pointerTracker.updatePointers(0.001)

		// Fluid simulation steps
		this.stepFluid(dt)
	}

	private stepFluid(dt: number): void {
		const gl = this.gl
		// Disable blending for simulation steps
		gl.disable(gl.BLEND)

		// Curl step
		this.curlProgram.bind()
		gl.uniform2f(
			this.curlProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		gl.uniform1i(
			this.curlProgram.uniforms.uVelocity,
			this.velocityFBO.read.attach(0)
		)
		blit(gl, this.curlFBO)

		// Vorticity step
		this.vorticityProgram.bind()
		gl.uniform2f(
			this.vorticityProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		gl.uniform1i(
			this.vorticityProgram.uniforms.uVelocity,
			this.velocityFBO.read.attach(0)
		)
		gl.uniform1i(this.vorticityProgram.uniforms.uCurl, this.curlFBO.attach(1))
		gl.uniform1f(this.vorticityProgram.uniforms.curl, this.config.CURL)
		gl.uniform1f(this.vorticityProgram.uniforms.dt, dt)
		blit(gl, this.velocityFBO.write)
		this.velocityFBO.swap()

		// Divergence step
		this.divergenceProgram.bind()
		gl.uniform2f(
			this.divergenceProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		gl.uniform1i(
			this.divergenceProgram.uniforms.uVelocity,
			this.velocityFBO.read.attach(0)
		)
		blit(gl, this.divergenceFBO)

		// Clear pressure
		this.clearProgram.bind()
		gl.uniform1i(
			this.clearProgram.uniforms.uTexture,
			this.pressureFBO.read.attach(0)
		)
		gl.uniform1f(this.clearProgram.uniforms.value, this.config.PRESSURE)
		blit(gl, this.pressureFBO.write)
		this.pressureFBO.swap()

		// Pressure solve
		this.pressureProgram.bind()
		gl.uniform2f(
			this.pressureProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		gl.uniform1i(
			this.pressureProgram.uniforms.uDivergence,
			this.divergenceFBO.attach(0)
		)
		for (let i = 0; i < this.config.PRESSURE_ITERATIONS; i++) {
			gl.uniform1i(
				this.pressureProgram.uniforms.uPressure,
				this.pressureFBO.read.attach(1)
			)
			blit(gl, this.pressureFBO.write)
			this.pressureFBO.swap()
		}

		// Gradient subtract
		this.gradientSubtractProgram.bind()
		gl.uniform2f(
			this.gradientSubtractProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		gl.uniform1i(
			this.gradientSubtractProgram.uniforms.uPressure,
			this.pressureFBO.read.attach(0)
		)
		gl.uniform1i(
			this.gradientSubtractProgram.uniforms.uVelocity,
			this.velocityFBO.read.attach(1)
		)
		blit(gl, this.velocityFBO.write)
		this.velocityFBO.swap()

		// Advect velocity
		this.advectionProgram.bind()
		gl.uniform2f(
			this.advectionProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		if (!this.ext.supportLinearFiltering) {
			gl.uniform2f(
				this.advectionProgram.uniforms.dyeTexelSize,
				this.velocityFBO.read.texelSizeX,
				this.velocityFBO.read.texelSizeY
			)
		}
		gl.uniform1i(
			this.advectionProgram.uniforms.uVelocity,
			this.velocityFBO.read.attach(0)
		)
		gl.uniform1i(
			this.advectionProgram.uniforms.uSource,
			this.velocityFBO.read.attach(0)
		)
		gl.uniform1f(this.advectionProgram.uniforms.dt, dt)

		gl.uniform1f(
			this.advectionProgram.uniforms.dissipation,
			this.config.VELOCITY_DISSIPATION
		)

		blit(gl, this.velocityFBO.write)
		this.velocityFBO.swap()

		// Advect dye
		gl.uniform2f(
			this.advectionProgram.uniforms.texelSize,
			this.velocityFBO.read.texelSizeX,
			this.velocityFBO.read.texelSizeY
		)
		if (!this.ext.supportLinearFiltering) {
			gl.uniform2f(
				this.advectionProgram.uniforms.dyeTexelSize,
				this.dyeFBO.read.texelSizeX,
				this.dyeFBO.read.texelSizeY
			)
		}
		gl.uniform1i(
			this.advectionProgram.uniforms.uVelocity,
			this.velocityFBO.read.attach(0)
		)
		gl.uniform1i(
			this.advectionProgram.uniforms.uSource,
			this.dyeFBO.read.attach(1)
		)

		gl.uniform1f(this.advectionProgram.uniforms.dt, dt)
		gl.uniform1f(
			this.advectionProgram.uniforms.dissipation,
			this.config.DENSITY_DISSIPATION
		)

		blit(gl, this.dyeFBO.write)
		this.dyeFBO.swap()
	}

	render(): void {
		const gl = this.gl

		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
		gl.enable(gl.BLEND)

		const width = gl.drawingBufferWidth
		const height = gl.drawingBufferHeight

		this.displayMaterial.bind()
		if (this.config.SHADING) {
			this.displayMaterial.setKeywords(["SHADING"])
		} else {
			this.displayMaterial.setKeywords([])
		}
		this.displayMaterial.bind()

		if (this.config.SHADING) {
			gl.uniform2f(
				this.displayMaterial.uniforms.texelSize,
				1.0 / width,
				1.0 / height
			)
		}
		gl.uniform1i(
			this.displayMaterial.uniforms.uTexture,
			this.dyeFBO.read.attach(0)
		)
		blit(gl, null)
	}

	splat(
		x: number,
		y: number,
		dx: number,
		dy: number,
		color?: [number, number, number]
	): void {
		const gl = this.gl
		const splatColor = color || this.colorManager.generateRandomColor()

		this.splatProgram.bind()
		gl.uniform1i(
			this.splatProgram.uniforms.uTarget,
			this.velocityFBO.read.attach(0)
		)
		gl.uniform1f(
			this.splatProgram.uniforms.aspectRatio,
			this.canvas.width / this.canvas.height
		)
		gl.uniform2f(this.splatProgram.uniforms.point, x, y)
		gl.uniform3f(this.splatProgram.uniforms.color, dx, dy, 0.0)
		gl.uniform1f(
			this.splatProgram.uniforms.radius,
			this.config.SPLAT_RADIUS / 100.0
		)
		blit(gl, this.velocityFBO.write)
		this.velocityFBO.swap()

		gl.uniform1i(this.splatProgram.uniforms.uTarget, this.dyeFBO.read.attach(0))
		gl.uniform3f(
			this.splatProgram.uniforms.color,
			splatColor[0],
			splatColor[1],
			splatColor[2]
		)
		blit(gl, this.dyeFBO.write)
		this.dyeFBO.swap()
	}

	addRandomSplats(count: number): void {
		for (let i = 0; i < count; i++) {
			const color = this.colorManager.generateRandomColor()
			const x = Math.random()
			const y = Math.random()
			const dx = this.config.SPLAT_FORCE * 0.1 * (Math.random() - 0.5)
			const dy = this.config.SPLAT_FORCE * 0.1 * (Math.random() - 0.5)
			this.splat(x, y, dx, dy, color)
		}
	}

	updateConfig(newConfig: Partial<FluidConfig>): void {
		this.config = { ...this.config, ...newConfig }

		// Update color manager if color mode changed
		if (newConfig.COLOR_MODE && this.colorManager) {
			this.colorManager.setColorMode(newConfig.COLOR_MODE)
		}

		// Update fixed color if color property changed
		if (newConfig.COLOR !== undefined && this.colorManager) {
			if (newConfig.COLOR) {
				const parsedColor = parseCSSColor(newConfig.COLOR)
				if (parsedColor) {
					this.colorManager.setFixedColor(parsedColor)
				} else {
					console.warn(`Invalid color value: ${newConfig.COLOR}`)
					this.colorManager.clearFixedColor()
				}
			} else {
				this.colorManager.clearFixedColor()
			}
		}
	}

	handleResize(_width: number, _height: number): void {
		// Reinitialize FBOs with new dimensions
		this.initializeFBOs()
	}

	private calcDeltaTime(): number {
		const now = Date.now()
		let dt = (now - this.lastTime) / 1000
		dt = Math.min(dt, 0.016666) // Clamp to 60 FPS
		this.lastTime = now
		return dt
	}

	destroy(): void {
		this.pointerTracker?.destroy()
	}
}
